import React, { FunctionComponent, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ISupportRequestPayload } from '../../../../../app/api/request-config/support.api';
import { submitSupportRequest } from '../../../../../app/support/support.actions';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { FileInput } from '../../inputs/FileInput';
import { SelectInput } from '../../inputs/SelectInput';
import { FormTextInput } from '../../inputs/TextInput';
import { ISupportFormData, IUserAgentData } from './types';

const TOPIC_OPTIONS = [
  { value: 'product_problems', label: 'Problems with the product' },
  { value: 'account_and_billing', label: 'Account and billing' },
  { value: 'general', label: 'General' },
];

const collectSupportDeviceDetails = (): Pick<
  ISupportRequestPayload,
  'page_url' | 'browser' | 'os' | 'language' | 'timezone'
> => {
  const userAgentData = (navigator as Navigator & { userAgentData?: IUserAgentData }).userAgentData;
  const brands = userAgentData && userAgentData.brands ? userAgentData.brands : [];
  const browser = brands
    .map((brand) => `${brand.brand} ${brand.version}`.trim())
    .filter((brand) => brand !== '')
    .join(', ');

  return {
    page_url: window.location.href,
    ...(browser ? { browser } : {}),
    os: (userAgentData && userAgentData.platform) || navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

export const SupportForm: FunctionComponent = () => {
  const dispatch = useDispatch();
  const formProps = useForm<ISupportFormData>();
  const requiredValidation = useRequiredValidation();
  const descriptionValidation = useMemo(
    () => ({
      required: 'This field is required',
      minLength: { value: 10, message: 'Description must be at least 10 characters' },
    }),
    [],
  );

  const onSubmit = (data: ISupportFormData) => {
    const image = typeof data.image === 'string' ? data.image.trim() : '';
    dispatch(
      submitSupportRequest({
        topic: data.topic.value,
        description: data.description.trim(),
        ...(image ? { image } : {}),
        ...collectSupportDeviceDetails(),
      }),
    );
  };

  return (
    <FormProvider {...formProps}>
      <form id="support-form" className="topic-form" onSubmit={formProps.handleSubmit(onSubmit)} method="post">
        <div className="field-container">
          <SelectInput
            id="topic"
            name="topic"
            label="Topic"
            placeholder="Select a topic..."
            options={TOPIC_OPTIONS}
            validation={requiredValidation}
            isSingle
            onInputChange={() => undefined}
          />
        </div>
        <div className="field-container">
          <FormTextInput
            id="description"
            name="description"
            label="Describe your issue"
            placeholder="Please describe your issue..."
            validation={descriptionValidation}
            multi
          />
        </div>
        <div className="field-container">
          <FileInput
            id="image"
            name="image"
            label="Attach an image (optional)"
            maxFileSize={5}
            accept=".png,.jpg,.jpeg,.gif,.webp"
            acceptableFormats="png, jpg, gif, webp"
            showPreview
            storeInUserForm={false}
          />
        </div>
      </form>
    </FormProvider>
  );
};
